import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const AvatarWrapper = styled.div<{
  $height?: number;
  $width?: number;
}>`
  background: #f0f0f0;
  border-radius: 50%;
  flex-shrink: 0;
  height: ${({ $height }) => $height ? `${$height}px` : '40px'};
  overflow: hidden;
  position: relative;
  width: ${({ $width }) => $width ? `${$width}px` : '40px'};

  .image {
    border-radius: 50%;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const IconWrapper = styled.div<{
  $isAnonymous?: boolean;
  $randomColor?: string;
  $height?: number;
  $width?: number;
}>`
  align-items: center;
  display: flex;
  height: ${({ $height }) => $height ? `${$height}px` : '40px'};
  justify-content: center;
  width: ${({ $width }) => $width ? `${$width}px` : '40px'};
  flex-shrink: 0;
  background: ${({ $isAnonymous, $randomColor }) => $isAnonymous ? $randomColor || '#cccccc' : '#cccccc'};
`;

const InitialName = styled.span<{
  $fontSize?: number;
}>`
  color: #ffffff;
  font-size: ${({ $fontSize }) => $fontSize ? `${$fontSize}px` : '14px'};
  font-weight: 500;
`;

const PersonIconSvg = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#999999"/>
  </svg>
);

export interface IProfileAvatarProps {
  imageUrl?: string;
  fullName?: string;
  imageHeight?: number;
  imageWidth?: number;
  fontSize?: number;
  onClick?: () => void;
  style?: React.CSSProperties;
  forceLoad?: boolean;
}

const getInitials = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getRandomColor = (name?: string): string => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
    '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788'
  ];

  if (!name) return colors[0];

  const charCodeSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return colors[charCodeSum % colors.length];
};

const ProfileAvatarComponent: React.FC<IProfileAvatarProps> = ({
  imageUrl,
  fullName,
  imageHeight,
  imageWidth,
  fontSize,
  onClick,
  style,
  forceLoad = false
}) => {
  const [imageError, setImageError] = useState<boolean>(false);
  const [inView, setInView] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const initials = getInitials(fullName);
  const randomColor = getRandomColor(fullName);
  const shouldLoadImage = forceLoad || inView;

  // Lazy-load images only when avatar enters the viewport to avoid loading
  // hundreds/thousands of images at once.
  useEffect(() => {
    if (forceLoad) return;
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
            break;
          }
        }
      });
      io.observe(el);
      return () => io.disconnect();
    } else {
      // Fallback: mark as in view immediately
      setInView(true);
    }
  }, [forceLoad]);

  return (
    <AvatarWrapper
      $height={imageHeight}
      $width={imageWidth}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default', ...style }}
      ref={wrapperRef}
    >
      {imageUrl && !imageError && shouldLoadImage ? (
        <img
          className="image"
          src={imageUrl}
          alt={fullName ?? ''}
          loading={forceLoad ? "eager" : "lazy"}
          crossOrigin="anonymous"
          onError={() => setImageError(true)}
        />
      ) : fullName ? (
        <IconWrapper
          $isAnonymous={true}
          $randomColor={randomColor}
          $height={imageHeight}
          $width={imageWidth}
        >
          <InitialName $fontSize={fontSize}>
            {initials}
          </InitialName>
        </IconWrapper>
      ) : (
        <IconWrapper
          $height={imageHeight}
          $width={imageWidth}
        >
          <PersonIconSvg />
        </IconWrapper>
      )}
    </AvatarWrapper>
  );
};
// Memoize to avoid re-rendering avatars when parent lists paginate or chunk-render
const ProfileAvatar = React.memo(ProfileAvatarComponent);
export default ProfileAvatar;
